import { i18n } from "../boot/i18n";
import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import CustomerInvoice from "./CustomerInvoice";
import SaleItem from "./SaleItem";
import Email from "./Email";
import Total, { computeTotal } from "./InvoiceTotal";
import CustomerTerms from "./CustomerTerms";
import { ShipTo, BillTo } from "./BillTo";
import DocumentRef from "./DocumentRef";
import COLORS from "../colors";
import {
  deepFreeze,
  formatCurrency,
  formatPercentage,
  isDue,
  roundPrice,
  timestampToDate,
} from "../util/misc";
import {
  cloneDeep,
  compact,
  every,
  get,
  isEmpty,
  isString,
  last,
  map,
  omit,
  sortBy,
  uniq,
} from "lodash";
import { diff } from "deep-object-diff";
import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  parse,
  setDate,
} from "date-fns";
import { LOCATION_INFO } from "../constants";
import InvoicePDF from "../pdf/InvoicePDF";

export const INVOICE_SETTINGS = {
  removeLineBreaks: true,
  itemPrices: true,
};
deepFreeze(INVOICE_SETTINGS);

export const INVOICE_FIELDS = {
  creditMemo: false,
  aia: false,
  description: null,
  aiaDocument: null,
  bills: [],
  events: [],
  location: null,
  billTo: null,
  shipTo: null,
  saleItems: {},
  saleLabors: [],
  miscItems: [],
  manualInvoiceTypes: [],
  notax: false,
  tax: null,
  terms: null,
  total: null,
  salePaymentsApplied: 0,
  invoiceDate: null,
  dueDate: null,
  document: null,
  voidedDate: null,
  voidedReason: "",
  // Late-payment finance-charge assessment. `null` until an employee decides
  // to assess a charge on a past-due invoice; see `FINANCE_CHARGE_FIELDS`.
  // The accrued dollar amount lives on `total.financeCharge`; this record
  // holds the decision + audit metadata and the `lastAccrued` guard the daily
  // accrual job uses.
  financeCharge: null,
  sent: {},
  settings: INVOICE_SETTINGS,
  locked: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(INVOICE_FIELDS);

// Shape of the `Invoice.financeCharge` assessment record. Set when an employee
// assesses a late-payment finance charge and cleared (back to null) when the
// charge is removed. `rate`/`graceDays` snapshot the account terms at the time
// of assessment so a later terms edit can't retroactively change what was
// charged. `startDate` is the first day the charge accrues (invoiceDate +
// graceDays). `lastAccrued` is advanced by the daily accrual job so a day is
// never posted twice.
export const FINANCE_CHARGE_FIELDS = {
  assessedBy: null,
  assessedDate: null,
  startDate: null,
  rate: null,
  graceDays: null,
  lastAccrued: null,
};
deepFreeze(FINANCE_CHARGE_FIELDS);

// Fallback rate (monthly %) and grace period (days) used when an invoice's
// terms don't define finance-charge fields (e.g. a legacy invoice). Mirror the
// account-terms defaults in CustomerTerms.js and the server fallbacks in
// functions/invoices.js.
export const DEFAULT_FINANCE_CHARGE_RATE = 2;
export const DEFAULT_FINANCE_CHARGE_GRACE_DAYS = 45;

export const INVOICE_STATUS_TYPES = {
  unpaid: {
    order: 1,
    label: "Due",
    icon: "due_date",
    color: "warning",
  },
  "near-due": {
    order: 2,
    label: "Due",
    icon: "due_date",
    color: "negative",
  },
  due: {
    order: 2,
    label: "Due",
    icon: "due_date",
    color: "negative",
  },
  paid: {
    order: 4,
    label: "Paid",
    icon: "paid",
    color: "positive",
  },
  voided: {
    order: 5,
    label: "Voided",
    icon: "cancel",
    color: "negative",
  },
};
deepFreeze(INVOICE_STATUS_TYPES);

export default class Invoice extends DataStructure {
  constructor(projectID, saleID, id, data = {}) {
    data = sanitize(data, INVOICE_FIELDS);

    // Convert to Date
    data.invoiceDate = timestampToDate(data.invoiceDate);
    data.dueDate = timestampToDate(data.dueDate);
    data.voidedDate = timestampToDate(data.voidedDate);
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    if (data.financeCharge) {
      data.financeCharge = sanitize(data.financeCharge, FINANCE_CHARGE_FIELDS);
      data.financeCharge.assessedDate = timestampToDate(
        data.financeCharge.assessedDate
      );
      data.financeCharge.startDate = timestampToDate(
        data.financeCharge.startDate
      );
      data.financeCharge.lastAccrued = timestampToDate(
        data.financeCharge.lastAccrued
      );
    }

    super(data);

    this.projectID = projectID;
    this.saleID = saleID;
    this.id = id;
    this.permissions = Object.freeze({
      view: Invoice.canBeViewedBy,
      create: Invoice.canBeCreatedBy,
      edit: this.canBeEditedBy,
      send: this.canBeSentBy,
      delete: this.canBeDeletedBy,
      unlock: this.canBeUnlockedBy,
      void: this.canBeVoidedBy,
    });

    // Defaults
    this.data.billTo = this.billTo.data;
    this.data.shipTo = this.shipTo.data;
    this.data.terms = this.terms.data;
    this.data.total = this.total.data;

    if (this.data.terms && this.data.invoiceDate && !this.data.dueDate) {
      this.data.dueDate = this.getDueDate();
    }

    this.data.sent = this.sent.data;

    // Documents
    if (this.data.document) {
      this.data.document = new DocumentRef(this.data.document).data;
    }
    if (this.data.aiaDocument) {
      this.data.aiaDocument = new DocumentRef(this.data.aiaDocument).data;
    }
  }

  // Permissions

  static canBeViewedBy(user) {
    if (!user) return false;
    // Handle both User instances (with isEmployee getter) and plain objects
    if (typeof user.isEmployee === "boolean") return user.isEmployee;
    if (typeof user.isEmployee === "function") return user.isEmployee();
    // Fallback: check roles directly
    if (user.data?.roles) return !user.data.roles.includes("customer");
    return false;
  }

  static canBeCreatedBy(user) {
    return Invoice.canBeViewedBy(user);
  }

  canBeEditedBy(user) {
    const sale = this.sale;
    return (
      !this.data.voidedDate &&
      !this.data.events.length &&
      !this.data.locked &&
      sale &&
      !sale.data.locked &&
      user.isEmployee
    );
  }

  canBeSentBy(user) {
    const sale = this.sale;
    return (
      !this.data.voidedDate &&
      !this.data.aia &&
      sale &&
      !sale.data.locked &&
      user.isEmployee
    );
  }

  canBeDeletedBy(user) {
    const sale = this.sale;
    const payments = roundPrice(this.data.total?.payments || 0);
    const salePaymentsApplied = roundPrice(this.data.salePaymentsApplied || 0);
    return (
      !this.data.locked &&
      payments === salePaymentsApplied &&
      sale &&
      !sale.data.locked &&
      (user.isAdmin || user.id === this.data.createdBy)
    );
  }

  canBeUnlockedBy(user) {
    return user.isAdmin;
  }

  canBeVoidedBy(user) {
    const sale = this.sale;
    const payments = roundPrice(this.data.total.payments || 0);
    const salePaymentsApplied = roundPrice(this.data.salePaymentsApplied || 0);
    return (
      !this.data.voidedDate &&
      payments === salePaymentsApplied &&
      sale &&
      !sale.data.locked &&
      user.isEmployee
    );
  }

  // Database

  get path() {
    return Invoice.getPath(this.projectID, this.saleID, this.id);
  }

  static getPath(projectID, saleID, id) {
    let path = `projects/${projectID}/sales/${saleID}/invoices`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(invoice) {
    return invoice.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Invoice(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  get rules() {
    return {
      aiaDocument: [
        (a) =>
          !this.aia || Boolean(a && this.aiaDocument.isValid) || "required",
      ],
      dueDate: [(a) => Boolean(a) || "required"],
      invoiceDate: [(a) => Boolean(a) || "required"],
      manualInvoiceTypes: [
        (a) =>
          this.aia || (a.length > 0 && this.items.length > 0) || "required",
      ],
    };
  }

  get aia() {
    return this.data.aia;
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  computeTotal({
    quoteSubtotal,
    quoteDiscount,
    quotePrice,
    downPaymentAmount,
    downPaymentFee,
    priorDownPaymentAllocated,
    invoicePaymentAmount,
    invoicePaymentFee,
    invoiceAddDiscount,
    invoiceCredit,
    invoiceFinanceCharge,
    priorTaxAllocated,
    quoteTax,
  } = {}) {
    const items = this.items.map((item) => ({
      ...item.data,
      itemType: item.itemType,
    }));
    return computeTotal({
      quoteSubtotal,
      quoteDiscount,
      quotePrice,
      downPaymentAmount,
      downPaymentFee,
      priorDownPaymentAllocated,
      invoicePaymentAmount,
      priorTaxAllocated,
      quoteTax,
      // Preserve any fee / early-payment discount amounts that were
      // previously applied to this invoice so that re-saving (e.g.
      // InvoiceEdit) doesn't clobber `total.fee` / `total.addDiscount` —
      // touchInvoice and the sale-payment allocator only maintain those
      // fields during payment writes, not invoice writes. Callers that want
      // to reset them should explicitly pass 0.
      invoicePaymentFee:
        invoicePaymentFee !== undefined
          ? invoicePaymentFee
          : this.data.total?.fee,
      invoiceAddDiscount:
        invoiceAddDiscount !== undefined
          ? invoiceAddDiscount
          : this.data.total?.addDiscount,
      // Preserve any credit memo amounts that were previously applied to
      // this invoice so that re-saving (e.g. InvoiceEdit) doesn't clobber
      // `total.credit` — touchInvoice only maintains that field during CM
      // writes, not invoice writes. Callers that want to reset credit
      // should explicitly pass 0.
      invoiceCredit:
        invoiceCredit !== undefined ? invoiceCredit : this.data.total?.credit,
      // Preserve any accrued finance charge so re-saving (e.g. InvoiceEdit or
      // a PDF regenerate) doesn't clobber `total.financeCharge` — it is
      // maintained only by the assess/remove callable and the daily accrual
      // job, not by invoice writes. Callers that want to reset it should
      // explicitly pass 0.
      invoiceFinanceCharge:
        invoiceFinanceCharge !== undefined
          ? invoiceFinanceCharge
          : this.data.total?.financeCharge,
      items,
      tax: this.data.tax,
    });
  }

  getDueDate() {
    if (this.aia) {
      const netDays = 60;
      const dueDay = 20;
      let dueDate = setDate(this.invoiceDate, dueDay);
      if (this.invoiceDate.getDate() > dueDay) {
        dueDate = addMonths(dueDate, 1);
        dueDate.setDate(dueDay);
      }
      return addDays(dueDate, netDays);
    } else {
      const terms = this.terms;
      if (terms.cashSale || !terms.netDays) {
        return this.invoiceDate;
      } else {
        return addDays(this.invoiceDate, terms.netDays);
      }
    }
  }

  getAvailableDiscount(date) {
    if (isString(date)) {
      date = parse(date, i18n.global.t("format.date-standard"), new Date());
    }
    const buffer = 7;
    const terms = this.terms;
    if (terms.discount && terms.discountDays) {
      let discountDate = addDays(this.invoiceDate, terms.discountDays + buffer);
      if (date < discountDate) {
        return roundPrice(this.data.total.balance * (terms.discount / 100));
      }
    }
    return 0;
  }

  get availableDiscount() {
    return this.getAvailableDiscount(new Date());
  }

  get isEmpty() {
    const omittedFields = [
      "billTo",
      "dueDate",
      "invoiceDate",
      "location",
      "shipTo",
      "tax",
      "terms",
    ];
    return (
      !this.id &&
      isEmpty(
        diff(
          Invoice.sanitize(omit(this.data, omittedFields)),
          omit(new Invoice().data, omittedFields)
        )
      )
    );
  }

  isEqual(invoice) {
    return (
      this.id === invoice.id &&
      this.projectID === invoice.projectID &&
      this.saleID === invoice.saleID &&
      isEmpty(
        diff(
          Invoice.sanitize(cloneDeep(this.data)),
          Invoice.sanitize(cloneDeep(invoice.data))
        )
      )
    );
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (data.document) {
      const doc = new DocumentRef(data.document);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.document = null;
      }
    }
    if (data.aiaDocument) {
      const doc = new DocumentRef(data.aiaDocument);
      if (!doc[removeUnsaved ? "isSaved" : "isValid"]) {
        data.aiaDocument = null;
      }
    }
    return data;
  }

  static sanitize(data, removeUnsaved) {
    Invoice.cleanDocuments(data, removeUnsaved);
    return data;
  }

  // Exporting

  toCustomerInvoice() {
    const data = cloneDeep(this.data);
    data.projectID = this.projectID;
    data.saleID = this.saleID;

    // The customer mirror shows the accrued charge via `total.financeCharge`
    // but never carries the assessment record: only project invoices should
    // match the daily accrual query (see functions/invoices.js), and the
    // customer has no business seeing the internal audit metadata.
    delete data.financeCharge;

    const customer = this.billTo.address.contact;
    const customerID = customer.id;
    data.billTo.address.name = customer.name;
    delete data.billTo.address.contact;

    if (data.shipTo.type === "contact") {
      data.shipTo.address.name = customer.name;
      delete data.shipTo.address.contact;
    }

    // Remove sensitive or superfluous data
    data.items = [
      ...this.saleItems.map((item) => {
        let data = {
          ...item.data,
          id: item.id,
          itemType: "material",
        };
        delete data.cost;
        delete data.margin;
        delete data.retail;
        delete data.quantityReady;
        delete data.quantityScheduled;
        delete data.quantityDelivered;
        delete data.quantityInstalled;
        delete data.quantityInvoiced;
        delete data.fullyScheduled;
        delete data.fullyDelivered;
        delete data.fullyInstalled;
        delete data.fullyInvoiced;
        delete data.itemLocations;
        return data;
      }),
      ...this.saleLabors.map((labor) => {
        let data = { ...labor.data, id: labor.id, itemType: "labor" };
        delete data.cost;
        delete data.margin;
        delete data.laborers;
        delete data.status;
        return data;
      }),
      ...this.miscItems.map((item) => {
        let data = { ...item.data, id: item.id, itemType: "misc" };
        delete data.cost;
        delete data.margin;
        return data;
      }),
    ];

    return new CustomerInvoice(customerID, this.id, data);
  }

  // Formatting

  get project() {
    return store.state.main.projects[this.projectID];
  }

  get sale() {
    return store.state.main.sales[this.fullSaleID];
  }

  get fullSaleID() {
    return this.projectID + this.saleID;
  }

  get events() {
    return compact(this.data.events.map((id) => store.state.main.events[id]));
  }

  get location() {
    return this.data.location ? LOCATION_INFO[this.data.location] : null;
  }

  get billTo() {
    return this.data.billTo ? new BillTo(this.data.billTo) : new BillTo();
  }

  get shipTo() {
    return this.data.shipTo ? new ShipTo(this.data.shipTo) : new ShipTo();
  }

  get total() {
    return new Total(this.data.total);
  }

  get sortedCategories() {
    const categories = uniq(this.items.map((item) => item.category || "Other"));
    if (categories.length > 1 || categories[0] !== "Other") {
      return categories;
    }
    return [];
  }

  get terms() {
    return new CustomerTerms(this.data.terms);
  }

  // Finance charges

  // Grace period (days) before the charge accrues: the terms' `financeDays`,
  // falling back to the standard default for legacy terms that lack it.
  get financeChargeGraceDays() {
    const graceDays = this.terms.data.financeDays;
    return graceDays != null ? graceDays : DEFAULT_FINANCE_CHARGE_GRACE_DAYS;
  }

  // Default first day the finance charge accrues: the invoice date plus the
  // grace period. A manual assessment may override this to start earlier. Null
  // for cash sales (which never carry a finance charge) or when the invoice
  // has no date yet.
  get financeChargeStartDate() {
    if (this.terms.data.cashSale || !this.data.invoiceDate) {
      return null;
    }
    return addDays(this.data.invoiceDate, this.financeChargeGraceDays);
  }

  // Whether a finance charge MAY be assessed on this invoice. Any non-cash,
  // real (non-AIA, non-credit-memo) invoice that is not voided and still has a
  // balance qualifies — including legacy invoices whose terms predate the
  // finance-charge fields, which an employee can assess manually.
  get financeChargeEligible() {
    return Boolean(
      !this.terms.data.cashSale &&
        !this.aia &&
        !this.data.creditMemo &&
        !this.data.voidedDate &&
        roundPrice(this.data.total.balance) > 0
    );
  }

  // Whether a finance charge is currently assessed (turned on) on this invoice.
  get financeChargeAssessed() {
    return Boolean(this.data.financeCharge);
  }

  // Monthly finance-charge rate as a fraction (e.g. 0.02 for 2%). Uses the
  // snapshot on the assessment record when assessed, otherwise the live terms,
  // falling back to the standard default for legacy terms that lack a rate.
  get financeChargeMonthlyRate() {
    const rate = this.financeChargeAssessed
      ? this.data.financeCharge.rate
      : this.terms.data.financeCharge || DEFAULT_FINANCE_CHARGE_RATE;
    return rate ? rate / 100 : 0;
  }

  // The monthly rate as a display percentage, e.g. "2%".
  get financeChargeMonthlyPercent() {
    return formatPercentage(100 * this.financeChargeMonthlyRate);
  }

  // One day's finance charge on the given principal: monthly rate / 30, times
  // the principal. Non-compounding — callers pass the balance EXCLUDING any
  // already-accrued finance charge. Mirrors
  // `functions/invoices.js::computeFinanceChargeAccrual`.
  dailyFinanceCharge(principal) {
    return roundPrice((this.financeChargeMonthlyRate / 30) * (principal || 0));
  }

  // Today's accrual on the current principal (the balance excluding the
  // charge already accrued), formatted — the per-day figure the finance-charge
  // status bar shows. $0.00 once the principal is paid off.
  get financeChargeDailyAmount() {
    const total = this.data.total || {};
    const principal = roundPrice(
      (total.balance || 0) - (total.financeCharge || 0)
    );
    return formatCurrency(this.dailyFinanceCharge(Math.max(0, principal)));
  }

  // Number of days that have accrued (or would accrue) as of `asOf`, from the
  // finance-charge start date. Clamped at 0 before the start date.
  financeChargeDaysAccrued(asOf = new Date()) {
    const start =
      this.data.financeCharge?.startDate ?? this.financeChargeStartDate;
    if (!start) return 0;
    return Math.max(0, differenceInCalendarDays(asOf, start));
  }

  get document() {
    return this.data.document ? new DocumentRef(this.data.document) : null;
  }

  get aiaDocument() {
    return this.data.aiaDocument
      ? new DocumentRef(this.data.aiaDocument)
      : null;
  }

  get bills() {
    return compact(
      this.data.bills.map(
        (path) => store.state.main.bills[last(path.split("/"))]
      )
    );
  }

  get saleItems() {
    return sortBy(
      compact(
        map(this.data.saleItems, (quantity, id) => {
          let item = store.state.main.saleItems[id];
          return item
            ? new SaleItem(this.projectID, this.saleID, id, {
                ...item.data,
                quantity,
                price: item.data.price,
              })
            : null;
        })
      ),
      "line"
    );
  }

  get saleLabors() {
    return sortBy(
      compact(
        this.data.saleLabors.map(
          (id) => store.state.main.saleLabors[id] || null
        )
      ),
      "line"
    );
  }

  get miscItems() {
    return sortBy(
      compact(
        this.data.miscItems.map((id) => store.state.main.miscItems[id] || null)
      ),
      "line"
    );
  }

  get items() {
    return sortBy(
      [...this.saleItems, ...this.saleLabors, ...this.miscItems],
      "line"
    );
  }

  get status() {
    if (this.data.voidedDate) {
      return "voided";
    } else if (this.data.total.balance <= 0) {
      return "paid";
    } else if (isDue(this.data.dueDate)) {
      return "due";
    } else if (isDue(this.data.dueDate, 7)) {
      return "near-due";
    } else {
      return "unpaid";
    }
  }

  get statusType() {
    const type = INVOICE_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get sent() {
    return new InvoiceSent(this, this.data.sent);
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy]
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy && store.state?.main?.users
      ? store.state.main.users[this.data.modifiedBy]
      : null;
  }

  async createPDF(settings = this.data.settings) {
    const pdfCreator = new InvoicePDF();
    await pdfCreator.drawInvoice(this, settings);
    const pdf = pdfCreator.create();
    const fileName = `${i18n.global.tc("Invoice")} ${this.id}.pdf`;
    return new Promise((resolve, reject) => {
      pdf.getBlob((blob) => {
        try {
          const pdfURL = (window.URL || window.webkitURL).createObjectURL(blob);
          const pdfFile = new File([blob], fileName, {
            type: "application/pdf",
          });
          resolve({ pdf, pdfURL, pdfFile });
        } catch (error) {
          reject(error);
        }
      });
    });
  }

  toString() {
    return this.id;
  }
}

// Invoice Sent Status

export const INVOICE_SENT_FIELDS = {
  date: null,
  delivered: null,
  recipients: [],
  followups: [],
};
deepFreeze(INVOICE_SENT_FIELDS);

export const INVOICE_SENT_STATUS_TYPES = {
  unsent: {
    order: 1,
    label: "Ready to send",
    icon: "unsent",
    color: "warning",
  },
  undelivered: {
    order: 2,
    label: "Never delivered",
    icon: "undelivered",
    color: "negative",
  },
  sent: {
    order: 3,
    label: "Sent",
    icon: "sent",
    color: "positive",
  },
};
deepFreeze(INVOICE_SENT_STATUS_TYPES);

export class InvoiceSent extends DataStructure {
  constructor(parent, data = {}) {
    data = sanitize(data, INVOICE_SENT_FIELDS);

    // Convert to Date
    if (data.date) {
      data.date = timestampToDate(data.date);
    }
    if (data.delivered) {
      data.delivered = timestampToDate(data.delivered);
    }
    if (data.followups && data.followups.length) {
      data.followups.forEach((followup) => {
        followup.date = timestampToDate(followup.date);
      });
    }

    super(data);
    this._parent = parent;
  }

  get recipients() {
    return this.data.recipients.map((recipient) => ({
      contact: store.state.main.contacts[recipient.contact] || null,
      email: new Email(recipient.email),
    }));
  }

  get followups() {
    return this.data.followups.map((followup) => ({
      recipients: followup.recipients.map((recipient) => ({
        contact: store.state.main.contacts[recipient.contact] || null,
        email: new Email(recipient.email),
      })),
      date: followup.date,
    }));
  }

  get date() {
    return this.data.delivered || !this._parent.data.total.balance
      ? this.data.date || this._parent.data.created
      : null;
  }

  get status() {
    if (!this.data.date) {
      return "unsent";
    } else if (!this.data.delivered && this._parent.data.total.balance) {
      return "undelivered";
    } else {
      return "sent";
    }
  }

  get statusType() {
    const type = INVOICE_SENT_STATUS_TYPES[this.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }
}
